import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import { formatDate, formatToZMW, handlePrint, handleExcel, rootUrl } from '../../../helpers';
import { useAuth } from '../../../context/auth-context';
import { FaPrint, FaSpinner, FaFileExcel, FaFilePdf, FaChartBar, FaCalendarAlt, FaBuilding } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const Fiscal = ({startDate, endDate, triggerFetch}) => {
  const [cashData, setCashData] = useState([]);
  const [loading, setLoading] = useState(false);
  const {allData, setAllData} = useOutletContext();
  const auth = useAuth();
  const componentRef = useRef(null);

  const months = [
    { name: 'January', value: 0, quarter: 1 },
    { name: 'February', value: 1, quarter: 1 },
    { name: 'March', value: 2, quarter: 1 },
    { name: 'April', value: 3, quarter: 2 },
    { name: 'May', value: 4, quarter: 2 },
    { name: 'June', value: 5, quarter: 2 },
    { name: 'July', value: 6, quarter: 3 },
    { name: 'August', value: 7, quarter: 3 },
    { name: 'September', value: 8, quarter: 3 },
    { name: 'October', value: 9, quarter: 4 },
    { name: 'November', value: 10, quarter: 4 },
    { name: 'December', value: 11, quarter: 4 },
  ];

  const revenueCategories = [
    { id: "151", name: "LOCAL TAXES" },
    { id: "152", name: "FEES AND CHARGES" },
    { id: "153", name: "LICENCES" },
    { id: "154", name: "LEVIES" },
    { id: "155", name: "PERMITS" },
    { id: "156", name: "CHARGES" },
    { id: "157", name: "OTHER INCOMES" },
    { id: "158", name: "NATIONAL SUPPORT (GRANTS)" },
    { id: "159", name: "DONOR SUPPORT" },
  ];

  const expenditureCategories = [
    { id: "211", name: "Salaries" },
    { id: "212", name: "Wages" },
    { id: "213", name: "Allowances" },
    { id: "214", name: "Emolument Expenses" },
    { id: "221", name: "Office Costs" },
    { id: "222", name: "Building, Repair and Maintenance Costs" },
    { id: "223", name: "Plant, Machinery, Vehicle Running and Maintenance Costs" },
    { id: "224", name: "Other Administrative Operating Costs" },
    { id: "225", name: "Requisites" },
    { id: "226", name: "Services" },
    { id: "227", name: "Travel Expenses" },
    { id: "228", name: "Training" },
    { id: "229", name: "Legal Costs" },
  ];

  const sumOfArray = (arr) => arr.reduce((acc, num) => acc + num, 0);

  useEffect(() => {
    if (triggerFetch && startDate && endDate) {
      setLoading(true);
      axios
        .get(rootUrl(`/getCashByDate/${auth.user?.cid}~${startDate}~${endDate}`))
        .then((response) => {
          const cash = response.data.data;
          setCashData(cash?.cash || []);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching data', error);
          toast.error('Error fetching data');
          setLoading(false);
        });
    }
  }, [triggerFetch, startDate, endDate]);

  const getMonthData = (categoryId, monthIndex) => {
    if (!allData.budget || !cashData) return { actual: 0, budget: 0 };
    
    const start = new Date(startDate);
    const year = start.getFullYear();
    
    // Get budget entries for this category
    const budgetEntries = allData.budget.filter(
      item => item.year == year && item.coa?.toString().substring(0, 3) === categoryId
    );
    
    // Calculate budget for the month
    const monthlyBudget = sumOfArray(
      budgetEntries.map(item => parseFloat(item.price * item.frequency * item.qty) / 12)
    );
    
    // Get actual transactions for this month
    const monthStart = new Date(year, monthIndex, 1);
    const monthEnd = new Date(year, monthIndex + 1, 0, 23, 59, 59);
    
    const actual = sumOfArray(
      cashData
        .filter(item => {
          const itemDate = new Date(item.date);
          return itemDate >= monthStart && itemDate <= monthEnd &&
                 budgetEntries.some(entry => item.coa_id == entry.id);
        })
        .map(item => parseFloat(item.amount))
    );
    
    return { actual, budget: monthlyBudget };
  };

  const getQuarterData = (categoryId, quarter) => {
    const quarterMonths = months.filter(m => m.quarter === quarter);
    let totalActual = 0;
    let totalBudget = 0;
    
    quarterMonths.forEach(month => {
      const monthData = getMonthData(categoryId, month.value);
      totalActual += monthData.actual;
      totalBudget += monthData.budget;
    });
    
    return { actual: totalActual, budget: totalBudget };
  };

  const getAccumulatedData = (categoryId) => {
    let totalActual = 0;
    let totalBudget = 0;
    
    months.forEach(month => {
      const monthData = getMonthData(categoryId, month.value);
      totalActual += monthData.actual;
      totalBudget += monthData.budget;
    });
    
    return { actual: totalActual, budget: totalBudget };
  };

  const calculateVariance = (actual, budget) => {
    return actual - budget;
  };

  const calculateVariancePercent = (actual, budget) => {
    if (budget === 0) return 0;
    return ((actual - budget) / budget) * 100;
  };

  const renderCategoryRow = (category, isRevenue = true) => {
    const accumulated = getAccumulatedData(category.id);
    
    return (
      <tr key={category.id} className="hover:bg-blue-50 transition-colors">
        <td className="border border-gray-300 py-2 px-3 text-left font-medium text-gray-700">{category.name}</td>
        {months.map(month => {
          const monthData = getMonthData(category.id, month.value);
          const variance = calculateVariance(monthData.actual, monthData.budget);
          const variancePercent = calculateVariancePercent(monthData.actual, monthData.budget);
          
          return (
            <React.Fragment key={month.value}>
              <td className="border border-gray-300 py-2 px-2 text-right">{formatToZMW(monthData.actual)}</td>
              <td className="border border-gray-300 py-2 px-2 text-right">{formatToZMW(variance)}</td>
              <td className="border border-gray-300 py-2 px-2 text-right">{variancePercent.toFixed(1)}%</td>
              <td className="border border-gray-300 py-2 px-2 text-right bg-yellow-50">{formatToZMW(monthData.budget)}</td>
            </React.Fragment>
          );
        })}
        {[1, 2, 3, 4].map(quarter => {
          const quarterData = getQuarterData(category.id, quarter);
          const variance = calculateVariance(quarterData.actual, quarterData.budget);
          const variancePercent = calculateVariancePercent(quarterData.actual, quarterData.budget);
          
          return (
            <React.Fragment key={quarter}>
              <td className="border border-gray-300 py-2 px-2 text-right">{formatToZMW(quarterData.actual)}</td>
              <td className="border border-gray-300 py-2 px-2 text-right">{formatToZMW(variance)}</td>
              <td className="border border-gray-300 py-2 px-2 text-right">{variancePercent.toFixed(1)}%</td>
              <td className="border border-gray-300 py-2 px-2 text-right bg-yellow-50">{formatToZMW(quarterData.budget)}</td>
            </React.Fragment>
          );
        })}
        <td className="border border-gray-300 py-2 px-2 text-right">{formatToZMW(accumulated.actual)}</td>
        <td className="border border-gray-300 py-2 px-2 text-right">{formatToZMW(calculateVariance(accumulated.actual, accumulated.budget))}</td>
        <td className="border border-gray-300 py-2 px-2 text-right">{calculateVariancePercent(accumulated.actual, accumulated.budget).toFixed(1)}%</td>
        <td className="border border-gray-300 py-2 px-2 text-right bg-yellow-50">{formatToZMW(accumulated.budget)}</td>
      </tr>
    );
  };

  const renderTotalRow = (label, categories, isRevenue = true) => {
    let totalActual = 0;
    let totalBudget = 0;
    
    months.forEach(month => {
      categories.forEach(category => {
        const monthData = getMonthData(category.id, month.value);
        totalActual += monthData.actual;
        totalBudget += monthData.budget;
      });
    });
    
    const variance = calculateVariance(totalActual, totalBudget);
    const variancePercent = calculateVariancePercent(totalActual, totalBudget);
    
    return (
      <tr key={label} className={`bg-gradient-to-r ${isRevenue ? 'from-green-500 to-green-600' : 'from-red-500 to-red-600'} text-white font-bold`}>
        <td className="border border-gray-300 py-2 px-3">{label}</td>
        {months.map(month => {
          let monthActual = 0;
          let monthBudget = 0;
          categories.forEach(category => {
            const monthData = getMonthData(category.id, month.value);
            monthActual += monthData.actual;
            monthBudget += monthData.budget;
          });
          const monthVariance = calculateVariance(monthActual, monthBudget);
          const monthVariancePercent = calculateVariancePercent(monthActual, monthBudget);
          
          return (
            <React.Fragment key={month.value}>
              <td className="border border-gray-300 py-2 px-2 text-right">{formatToZMW(monthActual)}</td>
              <td className="border border-gray-300 py-2 px-2 text-right">{formatToZMW(monthVariance)}</td>
              <td className="border border-gray-300 py-2 px-2 text-right">{monthVariancePercent.toFixed(1)}%</td>
              <td className="border border-gray-300 py-2 px-2 text-right bg-yellow-50">{formatToZMW(monthBudget)}</td>
            </React.Fragment>
          );
        })}
        {[1, 2, 3, 4].map(quarter => {
          let quarterActual = 0;
          let quarterBudget = 0;
          categories.forEach(category => {
            const quarterData = getQuarterData(category.id, quarter);
            quarterActual += quarterData.actual;
            quarterBudget += quarterData.budget;
          });
          const quarterVariance = calculateVariance(quarterActual, quarterBudget);
          const quarterVariancePercent = calculateVariancePercent(quarterActual, quarterBudget);
          
          return (
            <React.Fragment key={quarter}>
              <td className="border border-gray-300 py-2 px-2 text-right">{formatToZMW(quarterActual)}</td>
              <td className="border border-gray-300 py-2 px-2 text-right">{formatToZMW(quarterVariance)}</td>
              <td className="border border-gray-300 py-2 px-2 text-right">{quarterVariancePercent.toFixed(1)}%</td>
              <td className="border border-gray-300 py-2 px-2 text-right bg-yellow-50">{formatToZMW(quarterBudget)}</td>
            </React.Fragment>
          );
        })}
        <td className="border border-gray-300 py-2 px-2 text-right">{formatToZMW(totalActual)}</td>
        <td className="border border-gray-300 py-2 px-2 text-right">{formatToZMW(variance)}</td>
        <td className="border border-gray-300 py-2 px-2 text-right">{variancePercent.toFixed(1)}%</td>
        <td className="border border-gray-300 py-2 px-2 text-right bg-yellow-50">{formatToZMW(totalBudget)}</td>
      </tr>
    );
  };

  const handleExportToPDF = () => {
    const input = componentRef.current;
    if (!input) return;
    
    html2canvas(input, { scrollY: -window.scrollY, scale: 0.5 }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape orientation
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const imgHeight = (canvasHeight * pdfWidth) / canvasWidth;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;
      
      while (heightLeft > 0) {
        position -= pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;
      }
      
      const fileName = `Fiscal_Report_${startDate}_${endDate}.pdf`;
      pdf.save(fileName);
    });
  };

  const handleExportToExcel = () => {
    const fileName = `Fiscal_Report_${startDate}_${endDate}`;
    handleExcel(componentRef, fileName);
  };

  if (loading) {
    return (
      <div className='flex flex-col justify-center items-center h-64 w-full bg-white rounded-2xl border border-gray-100 shadow-sm'>
        <FaSpinner size={50} className="animate-spin text-blue-950 mb-4" />
        <p className="text-gray-600 text-lg">Loading fiscal report...</p>
      </div>
    );
  }

  return (
    <div className="fiscal-report space-y-6">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className=" px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="bg-blue-950 rounded-lg p-2">
                <FaChartBar className="text-white text-xl" />
              </div>
              <div>
                <h2 className='font-bold text-xl'>{allData.council[0]?.name.toUpperCase()}</h2>
                <p className="text-gray-600 text-sm">Fiscal Performance Report</p>
              </div>
            </div>
            <div className="flex gap-2">
           
            </div>
          </div>
          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <FaCalendarAlt />
            <span>From {formatDate(startDate)} to {formatDate(endDate)}</span>
          </div>
        </div>

        <div ref={componentRef} className="overflow-x-auto p-6">
          <table className="min-w-full border-collapse border border-gray-300 text-xs">
            <thead>
              <tr className="bg-gradient-to-r from-blue-950 to-blue-800 text-white">
                <th rowSpan={2} className="border border-gray-300 py-2 px-3 text-left font-semibold">Category</th>
                {months.map(month => (
                  <th key={month.value} colSpan={4} className="border border-gray-300 py-2 px-2 text-center">
                    {month.name.substring(0, 3).toUpperCase()}
                  </th>
                ))}
                {[1, 2, 3, 4].map(quarter => (
                  <th key={quarter} colSpan={4} className="border border-gray-300 py-2 px-2 text-center">
                    Q{quarter}
                  </th>
                ))}
                <th colSpan={4} className="border border-gray-300 py-2 px-2 text-center">ACCUMULATED</th>
              </tr>
              <tr className="bg-gradient-to-r from-blue-800 to-blue-700 text-white">
                {[...Array(12)].map((_, i) => (
                  <React.Fragment key={i}>
                    <th className="border border-gray-300 py-1 px-1 text-center font-medium">Actual</th>
                    <th className="border border-gray-300 py-1 px-1 text-center font-medium">Variance</th>
                    <th className="border border-gray-300 py-1 px-1 text-center font-medium">Var %</th>
                    <th className="border border-gray-300 py-1 px-1 text-center font-medium">Budget</th>
                  </React.Fragment>
                ))}
                {[...Array(4)].map((_, i) => (
                  <React.Fragment key={i}>
                    <th className="border border-gray-300 py-1 px-1 text-center font-medium">Actual</th>
                    <th className="border border-gray-300 py-1 px-1 text-center font-medium">Variance</th>
                    <th className="border border-gray-300 py-1 px-1 text-center font-medium">Var %</th>
                    <th className="border border-gray-300 py-1 px-1 text-center font-medium">Budget</th>
                  </React.Fragment>
                ))}
                <th className="border border-gray-300 py-1 px-1 text-center font-medium">Actual</th>
                <th className="border border-gray-300 py-1 px-1 text-center font-medium">Variance</th>
                <th className="border border-gray-300 py-1 px-1 text-center font-medium">Var %</th>
                <th className="border border-gray-300 py-1 px-1 text-center font-medium">Budget</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-gradient-to-r from-green-100 to-green-50 font-bold border-b-2 border-green-300">
                <td colSpan={69} className="border border-gray-300 py-2 px-3 text-green-800 flex items-center gap-2">
                  <FaBuilding />
                  TOTAL REVENUE
                </td>
              </tr>
              {revenueCategories.map(category => renderCategoryRow(category, true))}
              {renderTotalRow('TOTAL REVENUE', revenueCategories, true)}
              
              <tr className="bg-gradient-to-r from-red-100 to-red-50 font-bold border-b-2 border-red-300">
                <td colSpan={69} className="border border-gray-300 py-2 px-3 text-red-800 flex items-center gap-2">
                  <FaBuilding />
                  TOTAL EXPENDITURE
                </td>
              </tr>
              {expenditureCategories.map(category => renderCategoryRow(category, false))}
              {renderTotalRow('TOTAL EXPENDITURE', expenditureCategories, false)}
              
              <tr className="bg-gradient-to-r from-blue-100 to-blue-50 font-bold border-b-2 border-blue-300">
                <td colSpan={69} className="border border-gray-300 py-2 px-3 text-blue-800 flex items-center gap-2">
                  <FaChartBar />
                  SURPLUS/DEFICIT
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Fiscal;

